/* =========================================================
	ANNUAL REPORT: INVESTMENT ALLOCATION DONUT CHART
	Modern Chart.js implementation for risk/return
	allocation visualization
========================================================= */

/**
 * Investment Allocation Donut Chart
 * Modern Chart.js implementation for risk/return allocation visualization
 */

class AllocationDonutChart {
   constructor(canvasId) {
      this.canvas = document.getElementById(canvasId);
      this.chart = null;

      if (!this.canvas) {
         console.error(`Canvas element with id "${canvasId}" not found`);
         return;
      }

      this.init();
   }

   init() {
      const data = this.getDataFromAttributes();
      if (data) {
         this.createChart(data);
      }
   }

   /**
    * Extract percentage data from canvas data attributes
    */
   getDataFromAttributes() {
      const low = parseInt(this.canvas.dataset.low) || 0;
      const medium = parseInt(this.canvas.dataset.medium) || 0;
      const high = parseInt(this.canvas.dataset.high) || 0;

      // Validate that percentages add up to 100
      const total = low + medium + high;
      if (total !== 100) {
         console.warn(
            `Allocation percentages don't add up to 100%. Got: ${total}%`
         );
      }

      return {
         low: low,
         medium: medium,
         high: high,
      };
   }

   /**
    * Create the Chart.js doughnut chart
    */
   createChart(data) {
      const ctx = this.canvas.getContext('2d');

      this.chart = new Chart(ctx, {
         type: 'doughnut',
         data: {
            labels: [
               `Low ${data.low}%`,
               `Medium ${data.medium}%`,
               `High ${data.high}%`,
            ],
            datasets: [
               {
                  data: [data.low, data.medium, data.high],
                  backgroundColor: [
                     '#005E6E', // Dark teal for Low Risk (10%)
                     '#50C9B5', // Teal for Medium Risk (55%)
                     '#E0684B', // Coral/salmon for High Risk (35%)
                  ],
                  borderWidth: 0,
                  cutout: '50%', // Creates the donut hole (thicker donut)
               },
            ],
         },
         options: {
            responsive: true,
            maintainAspectRatio: true,

            // Animation settings
            animation: {
               duration: 1000,
               easing: 'easeOutQuart',
            },

            // Plugin configurations
            plugins: {
               legend: {
                  display: false, // We'll create custom legend if needed
               },
               tooltip: {
                  backgroundColor: 'rgba(60, 60, 60, 0.8)',
                  padding: {
                     x: 12,
                     y: 8,
                  },
                  caretSize: 6,
                  callbacks: {
                     label: (context) => {
                        const label = context.label;
                        return label;
                     },
                  },
               },
               // Custom plugin to draw percentage labels on segments
               datalabels: false, // Disable default datalabels if present
            },

            // Remove default hover effects for cleaner look
            hover: {
               mode: null,
            },

            // Ensure chart stays circular
            aspectRatio: 1,
         },
         plugins: [
            {
               // Custom plugin to draw percentage text on each segment
               afterDatasetsDraw: (chart) => {
                  const ctx = chart.ctx;
                  const meta = chart.getDatasetMeta(0);

                  meta.data.forEach((arc, index) => {
                     const {
                        x,
                        y,
                        startAngle,
                        endAngle,
                        innerRadius,
                        outerRadius,
                     } = arc;

                     // Calculate the middle angle of the arc
                     const midAngle = (startAngle + endAngle) / 2;

                     // Calculate position for the text (middle of the arc)
                     const radius = (innerRadius + outerRadius) / 2;
                     const textX = x + Math.cos(midAngle) * radius;
                     const textY = y + Math.sin(midAngle) * radius;

                     // Get the percentage value
                     const percentage = chart.data.datasets[0].data[index];

                     // Style the text - matches site design
                     ctx.fillStyle = '#ffffff'; // White text
                     ctx.font =
                        '400 1rem calluna_sans, "Helvetica Neue", Helvetica, Arial, sans-serif';
                     ctx.textAlign = 'center';
                     ctx.textBaseline = 'middle';

                     // No shadow per design requirements
                     ctx.shadowColor = 'transparent';
                     ctx.shadowBlur = 0;
                     ctx.shadowOffsetX = 0;
                     ctx.shadowOffsetY = 0;

                     // Draw the percentage text
                     ctx.fillText(`${percentage}%`, textX, textY);

                     // Reset shadow
                     ctx.shadowColor = 'transparent';
                     ctx.shadowBlur = 0;
                     ctx.shadowOffsetX = 0;
                     ctx.shadowOffsetY = 0;
                  });
               },
            },
         ],
      });
   }

   /**
    * Update chart data (useful if percentages change)
    */
   updateData(newData) {
      if (this.chart) {
         this.chart.data.datasets[0].data = [
            newData.low,
            newData.medium,
            newData.high,
         ];
         this.chart.data.labels = [
            `Low ${newData.low}%`,
            `Medium ${newData.medium}%`,
            `High ${newData.high}%`,
         ];
         this.chart.update();
      }
   }

   /**
    * Destroy the chart instance
    */
   destroy() {
      if (this.chart) {
         this.chart.destroy();
         this.chart = null;
      }
   }
}

/**
 * Initialize all allocation donut charts
 */
function initializeAllocationCharts() {
   // Target Allocation Chart
   const targetChart = document.getElementById('target-allocation-chart');
   if (targetChart) {
      new AllocationDonutChart('target-allocation-chart');
   }

   // Can easily add more charts here if needed
   // const actualChart = document.getElementById('actual-allocation-chart');
   // if (actualChart) {
   //   new AllocationDonutChart('actual-allocation-chart');
   // }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
   document.addEventListener('DOMContentLoaded', initializeAllocationCharts);
} else {
   initializeAllocationCharts();
}

// Export for module usage
export { AllocationDonutChart, initializeAllocationCharts };
